import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import RowActionsMenu from '../../Components/RowActionsMenu';
import { CATEGORY_TABS, TAB_KEYS, matchesTab, countByCategory, applyFilters } from './tankCategories';
import { CreateTankDialog, MoveTankDialog, ReturnTankDialog, TankHistoryDialog, DeleteTankDialog, isLocked } from './TankActions';
import { duplicateInfo, codeYear, sortByNumberGroup, baseTankNo } from './tankSearch';
import { loadRoutesByCustomer, routeText } from './tankRoutes';
import CustomerLink from '../../Components/CustomerLink';
import { matchesPermission } from '../../Utils/userAuthority';

const STATUS_OPTIONS = [
    { value: '', label: 'ทั้งหมด' },
    { value: 'ACTIVE', label: 'ใช้งาน' },
    { value: 'LOST', label: 'หาย' },
    { value: 'SEARCHING', label: 'กำลังตามหา' },
    { value: 'NO_DATA', label: 'ไม่มีข้อมูล' },
    { value: 'RETURNED', label: 'คืนแล้ว' },
    { value: 'LENT', label: 'ยืม' },
    { value: 'DONATED', label: 'ยกให้' },
    { value: 'DAMAGED', label: 'ชำรุด' },
];

const HAS_CUSTOMER_OPTIONS = [
    { value: '', label: 'ทั้งหมด' },
    { value: 'true', label: 'มีลูกค้า' },
    { value: 'false', label: 'ไม่มีลูกค้า' },
];

// Delete button shows only with report:admin (customer:write is held by every sales account) · unknown permissions (old session) = let the server decide, as elsewhere.
const canDeleteNow = () => {
    try {
        const perms = JSON.parse(localStorage.getItem('user') || '{}')?.permissions;
        return !Array.isArray(perms) || perms.some((g) => matchesPermission(g, 'report:admin'));
    } catch (e) { return true; }
};

const PAGE_SIZE = 20;
// The registry is ~1k rows: load it whole so tab counts cover every tank, then filter + page client-side.
const FETCH_ALL_SIZE = 20000;
const NOTE_TRUNCATE_LENGTH = 40;

// Edit status options exclude the "ทั้งหมด" (all) placeholder used by the filter dropdown above.
const EDIT_STATUS_OPTIONS = STATUS_OPTIONS.filter((o) => o.value);

// Round 1 (2026-07-21) was read-only. Round 2 (Phase 2+3, same day, vector 2026-07-21 06:18Z)
// adds the edit action via the existing PATCH /api/tanks/{tankId} — no new endpoint. The status
// dropdown covers the lost-tank workflow (Phase 3) without a separate workflow engine, per vector.
const TankManagement = () => {
    const [allTanks, setAllTanks] = useState([]);
    const [page, setPage] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const tab = TAB_KEYS.includes(tabParam) ? tabParam : 'shop';
    const [loading, setLoading] = useState(true);
    // สายของร้านที่ถือถัง: customer code → route names, read live (a moved tank shows its new shop's route)
    const [routesByCustomer, setRoutesByCustomer] = useState(null);
    const [routesFailed, setRoutesFailed] = useState(false);
    const [error, setError] = useState('');

    const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
    const [hasCustomerFilter, setHasCustomerFilter] = useState(HAS_CUSTOMER_OPTIONS[0]);
    const [routeInput, setRouteInput] = useState('');
    const [routeFilter, setRouteFilter] = useState('');
    // One box for tank number / ปีรหัส / shop name; "เฉพาะเลขซ้ำ" lists copies of a number side by side.
    const [searchInput, setSearchInput] = useState('');
    const [query, setQuery] = useState('');
    const [onlyDuplicates, setOnlyDuplicates] = useState(false);
    const [deletingTank, setDeletingTank] = useState(null);
    const canDelete = canDeleteNow();

    const [editingTank, setEditingTank] = useState(null);
    const [editForm, setEditForm] = useState({ status: 'ACTIVE', datePlaced: '', note: '' });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    // Long notes (mostly from the historical import, which folded several source columns into
    // one free-text field) are hard to read inline in the table — truncate with a link to view
    // the full text, instead of blowing up row height for every row.
    const [viewingNote, setViewingNote] = useState(null);

    // Create / move / return / history dialogs (vector 2026-10-06) — see TankActions.js. Moving or
    // returning goes through dedicated endpoints so every change lands in tank_movements.
    const [showCreate, setShowCreate] = useState(false);
    const [movingTank, setMovingTank] = useState(null);
    const [returningTank, setReturningTank] = useState(null);
    const [historyTank, setHistoryTank] = useState(null);

    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            handleLogout();
            return null;
        }
        return JSON.parse(user)?.token;
    }, [handleLogout]);

    // route is debounced client-side (below) so this only fires once typing pauses, not per keystroke.
    const fetchTanks = useCallback(async () => {
        const token = checkToken();
        if (!token) return;

        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: '0', size: String(FETCH_ALL_SIZE) });

            const response = await fetch(`${API_BASE_URL}/customer/api/tanks?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                if (response.status === 403) {
                    handleLogout();
                    return;
                }
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to fetch tanks');
            }

            const data = await response.json();
            setAllTanks(data?.content || []);
        } catch (e) {
            console.error('Fetch tanks error:', e);
            setError('ไม่สามารถดึงข้อมูลถังได้');
            setAllTanks([]);
        } finally {
            setLoading(false);
        }
    }, [checkToken, handleLogout]);

    useEffect(() => {
        fetchTanks();
    }, [fetchTanks]);

    useEffect(() => {
        const token = checkToken();
        if (!token) return;
        let cancelled = false;
        loadRoutesByCustomer({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` })
            .then((m) => { if (!cancelled) { setRoutesByCustomer(m); setRoutesFailed(false); } })
            .catch(() => { if (!cancelled) setRoutesFailed(true); });
        return () => { cancelled = true; };
    }, [checkToken]);

    // Debounce free-text route search, reset to page 1 whenever any filter changes.
    useEffect(() => {
        const t = setTimeout(() => {
            setRouteFilter(routeInput.trim());
            setPage(0);
        }, 400);
        return () => clearTimeout(t);
    }, [routeInput]);

    useEffect(() => {
        const t = setTimeout(() => {
            setQuery(searchInput.trim());
            setPage(0);
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Counts honour the dropdown/route filters (so a tab's number = rows you would see) but not the tab itself.
    // Duplicates are judged on the whole registry, so a copy is flagged even if its twin sits on another tab.
    const dupInfo = useMemo(() => duplicateInfo(allTanks), [allTanks]);
    const filtered = useMemo(() => {
        const list = applyFilters(allTanks, {
            status: statusFilter?.value,
            hasCustomer: hasCustomerFilter?.value,
            route: routeFilter,
            routesByCustomer,
            query,
            onlyDuplicates,
        });
        return onlyDuplicates ? sortByNumberGroup(list) : list;
    }, [allTanks, statusFilter, hasCustomerFilter, routeFilter, routesByCustomer, query, onlyDuplicates]);
    const counts = useMemo(() => countByCategory(filtered), [filtered]);
    const tabTanks = useMemo(() => filtered.filter((t) => matchesTab(t, tab)), [filtered, tab]);
    const totalElements = tabTanks.length;
    const totalPages = Math.ceil(totalElements / PAGE_SIZE);
    const tanks = tabTanks.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const handleTabChange = (key) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', key);
            return next;
        }, { replace: true });
        setPage(0);
    };

    const handleStatusChange = (opt) => {
        setStatusFilter(opt || STATUS_OPTIONS[0]);
        setPage(0);
    };

    const handleHasCustomerChange = (opt) => {
        setHasCustomerFilter(opt || HAS_CUSTOMER_OPTIONS[0]);
        setPage(0);
    };

    const statusLabel = (value) => STATUS_OPTIONS.find((o) => o.value === value)?.label || value || '-';

    const openEdit = (tank) => {
        setSaveError('');
        setEditingTank(tank);
        setEditForm({
            status: tank.status || 'ACTIVE',
            datePlaced: tank.datePlaced || '',
            note: tank.note || '',
        });
    };

    const closeEdit = () => {
        setEditingTank(null);
        setSaveError('');
    };

    const handleSaveEdit = async () => {
        const token = checkToken();
        if (!token || !editingTank) return;

        setSaving(true);
        setSaveError('');
        try {
            const response = await fetch(`${API_BASE_URL}/customer/api/tanks/${encodeURIComponent(editingTank.tankId)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    status: editForm.status,
                    datePlaced: editForm.datePlaced || null,
                    note: editForm.note,
                }),
            });

            if (!response.ok) {
                if (response.status === 403) {
                    handleLogout();
                    return;
                }
                const errorText = await response.text();
                // The one validation rule staff will actually hit day-to-day (Tank.onSaveValidate,
                // customer-module) — translate it; anything else falls through as raw backend text.
                const friendly = /no customer.*note/i.test(errorText)
                    ? 'ถ้าไม่มีรหัสลูกค้า ต้องกรอกหมายเหตุอธิบายเหตุผล'
                    : errorText;
                throw new Error(friendly || 'บันทึกไม่สำเร็จ');
            }

            setEditingTank(null);
            fetchTanks();
        } catch (e) {
            console.error('Save tank error:', e);
            setSaveError(e.message || 'บันทึกไม่สำเร็จ — ตรวจสอบข้อมูลอีกครั้ง');
        } finally {
            setSaving(false);
        }
    };

    const afterAction = () => {
        setShowCreate(false);
        setMovingTank(null);
        setReturningTank(null);
        setDeletingTank(null);
        fetchTanks();
    };

    return (
        <div className="btc-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>จัดการถัง</h1>
                <button className="btc-btn" style={{ backgroundColor: '#E60012' }} onClick={() => setShowCreate(true)}>＋ สร้างถัง</button>
            </div>

            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <div style={{ minWidth: 200 }}>
                    <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>สถานะ</div>
                    <Select
                        options={STATUS_OPTIONS}
                        value={statusFilter}
                        onChange={handleStatusChange}
                        className="btc-selectBox-container"
                        classNamePrefix="btc-selectBox"
                        isSearchable={false}
                    />
                </div>
                <div style={{ minWidth: 200 }}>
                    <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>ลูกค้า</div>
                    <Select
                        options={HAS_CUSTOMER_OPTIONS}
                        value={hasCustomerFilter}
                        onChange={handleHasCustomerChange}
                        className="btc-selectBox-container"
                        classNamePrefix="btc-selectBox"
                        isSearchable={false}
                    />
                </div>
                <div className="btc-search-bar" style={{ margin: 0, minWidth: 260 }}>
                    <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>ค้นหา เลขถัง / ปีรหัส / ชื่อร้าน</div>
                    <input
                        type="text"
                        aria-label="ค้นหาถัง"
                        placeholder="เช่น 2567M01 หรือ ชื่อร้าน"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        style={{ width: '100%' }}
                    />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={onlyDuplicates} onChange={(e) => { setOnlyDuplicates(e.target.checked); setPage(0); }} />
                    เฉพาะเลขซ้ำ <span style={{ color: '#6B6B6B', fontSize: '0.85rem' }}>({dupInfo.groupCount} เลข · {dupInfo.rowCount} ใบ)</span>
                </label>
                <div className="btc-search-bar" style={{ margin: 0 }}>
                    <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>สาย</div>
                    <input
                        type="text"
                        placeholder="ค้นหาสาย..."
                        value={routeInput}
                        onChange={(e) => setRouteInput(e.target.value)}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>

            <div role="tablist" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '2px solid #E0E0E0', marginBottom: '1rem' }}>
                {CATEGORY_TABS.map((t) => {
                    const active = t.key === tab;
                    return (
                        <button
                            key={t.key}
                            role="tab"
                            aria-selected={active}
                            onClick={() => handleTabChange(t.key)}
                            style={{
                                background: active ? '#E60012' : 'transparent',
                                color: active ? '#fff' : '#000',
                                border: 'none',
                                borderRadius: '6px 6px 0 0',
                                padding: '8px 16px',
                                cursor: 'pointer',
                                fontWeight: active ? 700 : 400,
                            }}
                        >
                            {t.label} ({counts[t.key]})
                        </button>
                    );
                })}
            </div>

            {error && <p className="error">{error}</p>}

            {loading ? (
                <p>Loading...</p>
            ) : (
                <>
                    <table className="btc-table">
                        <thead>
                        <tr>
                            <th>รหัสถัง</th>
                            <th>ปีรหัส</th>
                            <th>สถานะ</th>
                            <th>สาย</th>
                            <th>ยี่ห้อ</th>
                            <th>ชื่อลูกค้า</th>
                            <th>วันที่วางถัง</th>
                            <th>หมายเหตุ</th>
                            <th>ตัวเลือก</th>
                        </tr>
                        </thead>
                        <tbody>
                        {tanks.length === 0 ? (
                            <tr>
                                <td colSpan={9}>ไม่มีข้อมูลถังตรงตามเงื่อนไขที่เลือก</td>
                            </tr>
                        ) : (
                            tanks.map((tank) => {
                                const dup = dupInfo.isDuplicate(tank);
                                // identical tank_id on 2+ rows: the by-number endpoints (edit/move/return/history) can't
                                // tell the copies apart, so only delete (by row) is offered until the copies are told apart.
                                const ambiguous = dupInfo.isExactDuplicate(tank);
                                const year = codeYear(tank);
                                return (
                                <tr key={tank.id || tank.tankId} style={dup ? { background: '#FFF8E1' } : undefined}>
                                    <td>
                                        {tank.tankId || '-'}
                                        {dup && <span title="เลขนี้มีมากกว่า 1 ใบในทะเบียน" style={{ marginLeft: 6, background: '#F5A623', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: '0.75rem' }}>ซ้ำ</span>}
                                    </td>
                                    <td style={year.derived ? { color: '#6B6B6B' } : undefined} title={year.derived ? 'อ่านจากหน้าเลขถัง (ยังไม่ได้กรอกปีรหัส)' : undefined}>{year.text || '-'}</td>
                                    <td>{statusLabel(tank.status)}</td>
                                    <td>{routeText(tank, routesByCustomer, routesFailed)}</td>
                                    <td>{tank.brand || '-'}</td>
                                    <td>{tank.customerCode ? <CustomerLink name={tank.customerName} code={tank.customerCode} /> : '-'}</td>
                                    <td>{tank.datePlaced || '-'}</td>
                                    <td>
                                        {!tank.note ? '-' : tank.note.length <= NOTE_TRUNCATE_LENGTH ? (
                                            tank.note
                                        ) : (
                                            <>
                                                {tank.note.slice(0, NOTE_TRUNCATE_LENGTH)}...{' '}
                                                <span
                                                    style={{ color: '#E60012', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                    onClick={() => setViewingNote(tank.note)}
                                                >
                                                    ดูเพิ่มเติม
                                                </span>
                                            </>
                                        )}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {!ambiguous && !isLocked(tank) && tab === 'lent' && (
                                            <button className="btc-btn btn-sm" style={{ backgroundColor: '#E60012', marginRight: 6 }} onClick={() => setReturningTank(tank)}>ถอนคืนโรงงาน</button>
                                        )}
                                        {!ambiguous && !isLocked(tank) && (
                                            <button className="btc-btn btn-sm" style={{ backgroundColor: tab === 'lent' ? '#6B6B6B' : '#E60012', marginRight: 6 }} onClick={() => setMovingTank(tank)}>{tab === 'factory' ? 'ย้าย (ส่งให้ร้าน)' : 'ย้าย'}</button>
                                        )}
                                        {!ambiguous && !isLocked(tank) && tab !== 'lent' && (tank.customerCode || tank.status === 'LENT') && (
                                            <button className="btc-btn btn-sm" style={{ backgroundColor: '#6B6B6B', marginRight: 6 }} onClick={() => setReturningTank(tank)}>ถอนคืนโรงงาน</button>
                                        )}
                                        <RowActionsMenu
                                            actions={[
                                                ...(ambiguous ? [] : [
                                                    {
                                                        label: 'ประวัติ',
                                                        icon: <i className="fas fa-history"></i>,
                                                        onClick: () => setHistoryTank(tank),
                                                    },
                                                    {
                                                        label: 'แก้ไข',
                                                        icon: <i className="fas fa-edit"></i>,
                                                        onClick: () => openEdit(tank),
                                                    },
                                                ]),
                                                ...(canDelete ? [{
                                                    label: 'ลบ',
                                                    icon: <i className="fas fa-trash"></i>,
                                                    variant: 'danger',
                                                    onClick: () => setDeletingTank(tank),
                                                }] : []),
                                            ]}
                                        />
                                    </td>
                                </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                        <button
                            className="btc-btn btn-sm"
                            style={{ backgroundColor: '#6B6B6B' }}
                            disabled={page <= 0}
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                        >
                            ก่อนหน้า
                        </button>
                        <span style={{ color: '#000' }}>
                            หน้า <span style={{ color: '#E60012', fontWeight: 700 }}>{totalPages === 0 ? 0 : page + 1}</span> / {totalPages}
                            {' '}({totalElements} รายการ)
                        </span>
                        <button
                            className="btc-btn btn-sm"
                            style={{ backgroundColor: '#6B6B6B' }}
                            disabled={page >= totalPages - 1}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            ถัดไป
                        </button>
                    </div>
                </>
            )}

            {editingTank && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '24px', width: '420px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <div style={{ fontWeight: 600, fontSize: '16px' }}>แก้ไขถัง {editingTank.tankId}</div>
                            <button onClick={closeEdit} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B6B6B' }}>×</button>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>สถานะ</div>
                            <Select
                                options={EDIT_STATUS_OPTIONS}
                                value={EDIT_STATUS_OPTIONS.find((o) => o.value === editForm.status) || null}
                                onChange={(opt) => setEditForm((f) => ({ ...f, status: opt?.value || 'ACTIVE' }))}
                                className="btc-selectBox-container"
                                classNamePrefix="btc-selectBox"
                                isSearchable={false}
                            />
                        </div>

                        <div style={{ marginBottom: 12, fontSize: '0.9rem' }}>
                            ลูกค้าปัจจุบัน: <strong>{editingTank.customerCode ? <CustomerLink name={editingTank.customerName} code={editingTank.customerCode} /> : 'ไม่มี'}</strong>
                            <div style={{ color: '#6B6B6B', fontSize: '0.8rem' }}>เปลี่ยนร้าน → ใช้ปุ่ม "ย้าย" · คืนโรงงาน → ปุ่ม "ถอนคืนโรงงาน"</div>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>วันที่วางถัง</div>
                            <input
                                type="date"
                                value={editForm.datePlaced || ''}
                                onChange={(e) => setEditForm((f) => ({ ...f, datePlaced: e.target.value }))}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff', color: '#212529' }}
                            />
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <div style={{ color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 }}>หมายเหตุ {!editingTank.customerCode && <span style={{ color: '#E60012' }}>(จำเป็นเพราะถังไม่มีลูกค้า)</span>}</div>
                            <textarea
                                value={editForm.note}
                                onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))}
                                rows={3}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', background: '#fff', color: '#212529' }}
                            />
                        </div>

                        {saveError && <p className="error" style={{ marginBottom: 12 }}>{saveError}</p>}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: '#6B6B6B' }}
                                onClick={closeEdit}
                                disabled={saving}
                            >
                                ยกเลิก
                            </button>
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: '#E60012' }}
                                onClick={handleSaveEdit}
                                disabled={saving}
                            >
                                {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCreate && <CreateTankDialog onClose={() => setShowCreate(false)} onDone={afterAction} />}
            {movingTank && <MoveTankDialog tank={movingTank} onClose={() => setMovingTank(null)} onDone={afterAction} />}
            {returningTank && <ReturnTankDialog tank={returningTank} onClose={() => setReturningTank(null)} onDone={afterAction} />}
            {deletingTank && (
                <DeleteTankDialog
                    tank={deletingTank}
                    copies={allTanks.filter((t) => baseTankNo(t.tankId) === baseTankNo(deletingTank.tankId)).length}
                    onClose={() => setDeletingTank(null)}
                    onDone={afterAction}
                />
            )}
            {historyTank && <TankHistoryDialog tank={historyTank} onClose={() => setHistoryTank(null)} />}

            {viewingNote && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '24px', width: '420px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <div style={{ fontWeight: 600, fontSize: '16px' }}>หมายเหตุ</div>
                            <button onClick={() => setViewingNote(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B6B6B' }}>×</button>
                        </div>
                        <div style={{ color: '#000', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{viewingNote}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TankManagement;
